import { useEffect, useRef, useState } from 'react';
import { User, Palette, Settings, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { Blob } from '../render/Blob';
import { useSettingsModal } from './settings-store';

/**
 * Top-right account menu (styleguide §05 / Phase 6). The button is a morphing
 * Viv blob avatar; the menu shows identity + account rows. Reuses useAuth
 * (identity = email; handle = its local-part — there is no name field) and the
 * existing signOut. Brand kit / Settings don't exist yet → explicit disabled
 * stubs, never fake behavior.
 */
export function ProfileMenu() {
  const email = useAuth((s) => s.email);
  const signOut = useAuth((s) => s.signOut);
  const openSettings = useSettingsModal((s) => s.setOpen);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [open]);

  // Dev (Cognito disabled) has no email — show a friendly placeholder.
  const handle = email ? email.split('@')[0] : 'you';
  const sub = email ?? 'local dev';

  const rowCls = 'flex w-full items-center gap-2.5 min-h-[44px] px-2.5 rounded-[8px] border-0 bg-transparent text-left text-[15px] text-[var(--color-text)] enabled:hover:bg-[color-mix(in_oklab,var(--color-accent)_14%,transparent)] disabled:opacity-50 disabled:cursor-default';
  const iconCls = 'text-[var(--color-text-muted)] shrink-0';

  return (
    <div ref={wrapRef} className="fixed top-4 left-4 z-[60]">
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        aria-label={`Account menu — ${handle}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="block cursor-pointer rounded-full transition-transform hover:scale-105 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
        title={`${handle} — account`}
      >
        <Blob size={40} />
      </button>

      {open && (
        <div
          data-anim
          role="menu"
          aria-label="Account"
          className="cf-pop-in absolute left-0 top-[54px] min-w-[256px] p-2 rounded-[12px] bg-[var(--color-menu-bg)] border border-[var(--color-menu-border)] shadow-[var(--shadow-2)]"
          style={{ transformOrigin: 'top left' }}
        >
          <div className="flex items-center gap-2.5 px-2.5 pt-2 pb-3">
            <Blob size={34} />
            <span className="min-w-0 leading-tight">
              <b className="block text-[15px] text-[var(--color-text)]">{handle}</b>
              <span className="block text-[13px] text-[var(--color-text-muted)] truncate">{sub}</span>
            </span>
          </div>
          <div className="h-px mx-1.5 my-2 bg-[var(--color-border)]" />
          <button role="menuitem" className={rowCls} onClick={() => { setOpen(false); openSettings(true); }} title="Account & settings">
            <User size={17} className={iconCls} /> <span className="flex-1">Account</span>
            <ChevronRight size={15} className={iconCls} />
          </button>
          <button role="menuitem" className={rowCls} disabled title="Brand kit (coming soon)">
            <Palette size={17} className={iconCls} /> <span className="flex-1">Brand kit</span>
            <span className="text-[11px] text-[var(--color-text-muted)]">soon</span>
          </button>
          <button role="menuitem" className={rowCls} onClick={() => { setOpen(false); openSettings(true); }} title="Settings">
            <Settings size={17} className={iconCls} /> <span className="flex-1">Settings</span>
          </button>
          <div className="h-px mx-1.5 my-2 bg-[var(--color-border)]" />
          <button role="menuitem" className={rowCls} onClick={() => { setOpen(false); signOut(); }}>
            <LogOut size={17} className={iconCls} /> <span className="flex-1">Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
